import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { MonitorCardComponent } from '../monitor-card/monitor-card.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CameraService } from '../../../core/services/camera.service';
import { AlertService } from '../../../core/services/alert.service';
import { OrganizationService } from '../../../core/services/organization.service';
import { catchError, forkJoin, of, Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { TimelineService } from '../../../core/services/timeline.service';
import { GlobalSettingsService } from '../../../core/services/global-settings.service';
import { VehicleAlertFocusService } from '../../../core/services/vehicle-alert-focus.service';

@Component({
  selector: 'app-alert',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './alert.component.html',
  styleUrl: './alert.component.css'
})
export class AlertComponent implements OnChanges, OnInit, OnDestroy {
  @Input() notificationsList: any[] = [];
  eventBus: any;
  private timelineClickSub?: Subscription;

  selectedItemId: string | null = null;
  dismissedAlertsList: any[] = [];
  dismissedAlertPage: number = 1;
  alertSearchKeyword: string = '';
  isLoadingDismissed: boolean = false;
  modalOpen: boolean = false;
  @ViewChild('alertListContainer', { static: false }) alertListContainer?: ElementRef;

  constructor(private modalService: NgbModal,
    private camera: CameraService, 
    private alertservice: AlertService, 
    private organizationService: OrganizationService, 
    private timelineService: TimelineService,
    private globalSettings: GlobalSettingsService,
    private vehicleAlertFocus: VehicleAlertFocusService
  ) {
  }
  statusOptions: string[] = ['Active', 'Dismissed'];
  statusFilterName: string = this.statusOptions[0]; // default 'Active'
  ngOnChanges(changes: SimpleChanges): void {
  }
  ngOnInit() {
    this.timelineClickSub = this.timelineService.timelineClick$.subscribe(({ arcliveid, listid }) => {
      if (listid) {
        const newListId = listid;
        this.updateScrollbar('scrollTo', newListId);
        this.selectedItemId = newListId;
        this.animateItem(newListId);
      }
    });
  }

  ngOnDestroy() {
    this.timelineClickSub?.unsubscribe();
  }
  handleTimelineClick({ arcliveid, listid }: { arcliveid: any, listid: any }) {
    if (listid) {
      const newListId = '#' + listid;
      this.updateScrollbar('scrollTo', newListId);
      this.selectedItemId = newListId;
      this.animateItem(newListId);
    }
  }
  runAlert(event: MouseEvent, item: any): void {
    event?.stopPropagation?.();
    if (this.vehicleAlertFocus.focusFromAlert(item)) {
      return;
    }
    this.vehicleAlertFocus.leaveVehicleViewForStandardAlert();
    if (this.modalOpen) {
      return;
    }
    this.modalOpen = true;
    const newItem = { ...item };

    newItem.seeInfo = () => {
      if (typeof newItem.showInfo === 'function') {
        newItem.showInfo();
      }
    };

    const openModalWithData = (dataItem: any) => {
      const title = "Alert Details";
      this.handleFlyToUserLocation(dataItem);
      this.openDetailsModal(title, dataItem, true)
        .finally(() => {
          this.modalOpen = false;
        });
    };

    if (newItem.id) {
      forkJoin({
        userDetails: this.organizationService.getProfilesFromOrganization(newItem.userId).pipe(
          catchError(err => {
            console.error("Error fetching user details:", err);
            return of(null); // Provide fallback so forkJoin still completes
          })
        ),
        sopDetails: this.organizationService.getSOP(newItem.id).pipe(
          catchError(err => {
            console.error("Error fetching SOP details:", err);
            return of(null); // Provide fallback so forkJoin still completes
          })
        ),

        alertPayload: this.alertservice.getAlertById(newItem.id).pipe(
          catchError(err => {
            console.error("Error fetching alert details:", err);
            return of(null); // Provide fallback so forkJoin still completes
          })
        )
      }).subscribe(({ userDetails, alertPayload, sopDetails }) => {
        newItem.userDetails = userDetails;
        newItem.alertPayload = alertPayload;
        newItem.sopDetails = sopDetails;
        openModalWithData(newItem);
      });
    } else {
      openModalWithData(newItem);
    }
  }


  private openDetailsModal(
    title: string,
    data: any,
    shouldCallApi: boolean
  ) {
    const modalRef = this.modalService.open(MonitorCardComponent, {
      size: 'lg',
      centered: true,
      backdrop: 'static',
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
      keyboard: false,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.alertInfo = data;
    modalRef.componentInstance.showCancel = false;
    modalRef.componentInstance.shouldCallApi = shouldCallApi;
    
    return modalRef.result;
  }

  //map fly-to logic
  private handleFlyToUserLocation(alertInfo: any): void {
    const coordinates = alertInfo?.buildingInfo?.[0]?.room?.geometry?.coordinates;

    if (!coordinates || !Array.isArray(coordinates[0])) {
      const featureCoords = alertInfo?.AlertFeatures?.[0]?.geometry?.coordinates;
      if (Array.isArray(featureCoords) && typeof featureCoords[0] === 'number' && typeof featureCoords[1] === 'number') {
        this.camera.flyToLocation(featureCoords[1], featureCoords[0]);
      }
      return;
    }

    const coords = coordinates[0]; // first ring of polygon or coordinate array

    // If it's a single point (e.g. [lon, lat])
    if (Array.isArray(coords) && coords.length === 2 && typeof coords[0] === 'number') {
      const [longitude, latitude] = coords;
      this.camera.flyToLocation(latitude, longitude);
      return;
    }

    // If it's a polygon (array of [lon, lat] pairs)
    if (Array.isArray(coords) && Array.isArray(coords[0])) {
      const latitudes = coords.map(coord => coord[1]);
      const longitudes = coords.map(coord => coord[0]);

      const avgLat = latitudes.reduce((a, b) => a + b, 0) / latitudes.length;
      const avgLng = longitudes.reduce((a, b) => a + b, 0) / longitudes.length;

      this.camera.flyToLocation(avgLat, avgLng);
      return;
    }

  }
  updateScrollbar(action: string, selector: string) {
    const id = selector.startsWith('#') ? selector.slice(1) : selector;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el && action === 'scrollTo') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 0);
  }
  escapeIdForQuerySelector(id: string) {
    return `#${CSS.escape(id)}`;
  }

  animateItem(selector: string) {
    const id = selector.startsWith('#') ? selector.slice(1) : selector;
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        el.classList.add('wiggle-animation');
        setTimeout(() => {
          el.classList.remove('wiggle-animation');
        }, 1000);
      }
    }, 0);
  }

  onStatusFilterChange(status: string) {
    this.statusFilterName = status;
    if (status === 'Dismissed') {
      this.dismissedAlertPage = 1;
      this.alertSearchKeyword = '';
      this.fetchDismissedAlerts();
    }
  }

  fetchDismissedAlerts() {
    this.isLoadingDismissed = true;
    this.alertservice.findDismissedAlert(this.dismissedAlertPage, this.alertSearchKeyword, [])
      .subscribe({
        next: (alerts: any) => {
          this.dismissedAlertsList = alerts?.data || [];
          this.isLoadingDismissed = false;
        },
        error: () => {
          this.dismissedAlertsList = [];
          this.isLoadingDismissed = false;
        }
      });
  }

  onDismissedAlertPageChange(delta: number) {
    this.dismissedAlertPage += delta;
    this.fetchDismissedAlerts();
  }

  onDismissedAlertSearch() {
    this.dismissedAlertPage = 1;
    this.fetchDismissedAlerts();
  }

  onDismissedAlertSearchKeyup(event: KeyboardEvent) {
    this.onDismissedAlertSearch();
  }

  trackById(index: number, item: any) {
    return item.id;
  }

  isAlertChimeOn(){
		return this.globalSettings.getAlertChime();
	};

	toggleAlertSound(){
		this.globalSettings.setAlertChime(!this.isAlertChimeOn());
	};

}
